import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Check, Circle } from "lucide-react";
import { sprintService, apiErrorMessage } from "../../api/taskBoardService";
import { SectionCard, ErrorState, SkeletonRows } from "./shared";
import { STATUS_LABELS } from "./constants";
import SprintDocumentCard from "./SprintDocumentCard";
import useSprintDocument from "./useSprintDocument";
import { buildSprintTimelinePdf, downloadBlob, fmtDate, syncSprintTimelineDocument } from "./sprintDocuments";

/**
 * Sprint timeline in the same vertical style as "Timeline & milestones", listing
 * the sprint's tasks by their existing due dates (done tasks are checked and
 * struck through; there is no toggle).
 * Nothing is scheduled here and tasks have no start dates. Changes arrive
 * through `refreshKey` (realtime) and the list is re-read from the API.
 * `squad` / `assignee` narrow it like the board's filters.
 */
export const SprintTaskTimeline = ({ sprintId, refreshKey, squad = "", assignee = "", onLoaded }) => {
  const [tasks, setTasks] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      // From the API's sprint task set: for a finished sprint it never changes
      // when unfinished tasks are moved to a newer sprint.
      const data = (await sprintService.timeline(sprintId, { squad, assignee })).data;
      setTasks(data.tasks);
      onLoaded?.(data);
      setError("");
    } catch (err) {
      setError(apiErrorMessage(err, "Could not load the timeline"));
    }
  }, [sprintId, squad, assignee]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  // One timeline entry per due date: tasks due the same day share it.
  const { groups, undated } = useMemo(() => {
    const list = tasks || [];
    const byDay = new Map();
    list
      .filter((t) => t.dueDate)
      .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate) || (a.number || 0) - (b.number || 0))
      .forEach((t) => {
        const key = new Date(t.dueDate).toISOString().slice(0, 10);
        byDay.set(key, [...(byDay.get(key) || []), t]);
      });
    return {
      groups: [...byDay.entries()].map(([key, items]) => ({ key, date: items[0].dueDate, items })),
      undated: list.filter((t) => !t.dueDate),
    };
  }, [tasks]);

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!tasks) return <SkeletonRows rows={4} />;

  // Same look as the old milestones: a finished task gets a check and a strike-through.
  const entry = (key, date, items) => {
    const allDone = items.every((t) => t.status === "done");
    const late = items.some((t) => t.deadlineMissed && t.status !== "done");
    const dateLabel = date ? fmtDate(date) : "No due date";
    const dateClass = late ? "text-danger" : "text-gray-500 dark:text-light-500";
    const node = allDone ? (
      <span className="w-5 h-5 rounded-full bg-accent text-white flex items-center justify-center">
        <Check className="w-3 h-3" strokeWidth={3} />
      </span>
    ) : (
      <Circle className="w-5 h-5 text-faint" />
    );
    const title = (t) => (
      <span className={t.status === "done" ? "line-through text-muted" : "text-gray-900 dark:text-light-100"}>{t.title}</span>
    );

    return (
      <li key={key} className="relative ml-4">
        <span className="absolute -left-[26px] top-0.5 bg-white dark:bg-primary-card rounded-full">{node}</span>
        {items.length === 1 ? (
          <>
            <p className="text-sm font-medium break-words" title={STATUS_LABELS[items[0].status]}>{title(items[0])}</p>
            <p className={`text-xs ${dateClass}`}>{dateLabel}</p>
          </>
        ) : (
          <>
            {/* Several tasks share this deadline: the date is the heading, each task is its own row. */}
            <p className={`text-xs font-semibold ${dateClass}`}>
              {dateLabel}
              <span className="ml-2 font-normal text-faint">{items.length} tasks</span>
            </p>
            <ul className="mt-1.5 space-y-1.5 rounded-xl border border-line bg-panel2/40 p-2">
              {items.map((t) => (
                <li key={t._id} className="flex items-start gap-2 text-sm font-medium break-words" title={STATUS_LABELS[t.status]}>
                  {t.status === "done" ? (
                    <Check className="w-3.5 h-3.5 mt-[3px] shrink-0 text-accent" strokeWidth={3} />
                  ) : (
                    <span className="w-1.5 h-1.5 mt-[7px] mx-1 rounded-full shrink-0 bg-faint" />
                  )}
                  <span className="min-w-0">{title(t)}</span>
                </li>
              ))}
            </ul>
          </>
        )}
      </li>
    );
  };

  return tasks.length === 0 ? (
    <p className="text-sm italic text-gray-400 dark:text-light-500">No tasks in this sprint yet</p>
  ) : (
    <ol className="border-l border-gray-200 dark:border-dark-100 ml-1.5 space-y-3">
      {groups.map((g) => entry(g.key, g.date, g.items))}
      {undated.length > 0 && entry("no-date", null, undated)}
    </ol>
  );
};

const SprintTimelineTab = ({ sprint, admin, refreshKey, onSprintChange, showError, showSuccess }) => {
  // Saved-PDF state ({ hash, document }) comes with the timeline data.
  const [meta, setMeta] = useState(null);

  // Download is always built from a fresh read of the sprint's tasks.
  const download = async () => {
    try {
      const data = (await sprintService.timeline(sprint._id)).data;
      downloadBlob(buildSprintTimelinePdf({ sprint, tasks: data.tasks }), `${sprint.name.replace(/[^\w-]+/g, "-")}_Timeline.pdf`);
    } catch (err) {
      showError?.(apiErrorMessage(err, "Could not export the timeline"));
    }
  };

  const { busy, genError, regenerate } = useSprintDocument({
    admin,
    hash: meta?.hash,
    document: meta?.document,
    sync: () => syncSprintTimelineDocument(sprint),
    onSaved: onSprintChange,
    onDone: async () => setMeta((await sprintService.timeline(sprint._id)).data),
    showError,
    showSuccess,
    successMessage: "Timeline PDF saved to Documents",
  });

  return (
    <div className="space-y-4">
      <SprintDocumentCard
        title="Timeline document"
        doc={meta?.document}
        busy={busy}
        genError={genError}
        admin={admin}
        onDownload={download}
        onRegenerate={regenerate}
        emptyText="No PDF saved yet."
      />
      <SectionCard title="Timeline & milestones">
        <SprintTaskTimeline sprintId={sprint._id} refreshKey={refreshKey} onLoaded={setMeta} />
      </SectionCard>
    </div>
  );
};

export default SprintTimelineTab;
